import * as Phaser from "phaser"
import { EventBus } from "../core/event-bus"
import { EVENT, TEX } from "../core/keys"
import { BUMPER_TYPES } from "../data/constants"
import type { BumperEffectType } from "../types"

const drawUnifiedBumper = (
  ctx: CanvasRenderingContext2D,
  size: number,
  baseColor: string,
): void => {
  const color = Phaser.Display.Color.HexStringToColor(baseColor).color
  const br = (color >> 16) & 0xff
  const bg = (color >> 8) & 0xff
  const bb = color & 0xff
  const dr = Math.max(0, br - 60)
  const dg = Math.max(0, bg - 60)
  const db = Math.max(0, bb - 60)
  const lr = Math.min(255, br + 50)
  const lg = Math.min(255, bg + 50)
  const lb = Math.min(255, bb + 50)

  const center = size / 2
  const radius = size / 2

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const dx = x - center
      const dy = y - center
      const dist = Math.sqrt(dx * dx + dy * dy)
      if (dist > radius) continue

      const norm = dist / radius
      if (norm > 0.9) {
        ctx.fillStyle = `rgb(${dr},${dg},${db})`
      } else if (norm > 0.75) {
        ctx.fillStyle = baseColor
      } else if (norm > 0.55) {
        if (dx < 0 && dy < 0) {
          ctx.fillStyle = `rgb(${lr},${lg},${lb})`
        } else {
          ctx.fillStyle = baseColor
        }
      } else if (norm > 0.3) {
        ctx.fillStyle = `rgb(${lr},${lg},${lb})`
      } else {
        ctx.fillStyle = "rgb(255,255,255)"
      }
      ctx.fillRect(x, y, 1, 1)
    }
  }

  const cs = Math.max(2, Math.floor(radius * 0.2))
  ctx.fillStyle = "rgb(255,255,255)"
  ctx.fillRect(center - cs, center, cs * 2, 1)
  ctx.fillRect(center, center - cs, 1, cs * 2)

  const hl = Math.max(2, Math.floor(radius * 0.25))
  ctx.fillStyle = "rgb(255,255,255)"
  ctx.fillRect(center - hl, center - hl, 2, 1)
  ctx.fillRect(center - hl, center - hl + 1, 1, 1)
}

const drawFireballOrb = (
  ctx: CanvasRenderingContext2D,
  size: number,
  mainHex: number,
  lightHex: number,
  glowHex: number,
): void => {
  const toRgb = (h: number) =>
    [(h >> 16) & 0xff, (h >> 8) & 0xff, h & 0xff] as const
  const [gr, gg, gb] = toRgb(glowHex)
  const [mr, mg, mb] = toRgb(mainHex)
  const [lr, lg, lb] = toRgb(lightHex)

  const center = size / 2
  const radius = size / 2 - 1
  const outer = radius + 2

  for (
    let y = Math.floor(center - outer);
    y <= Math.ceil(center + outer);
    y++
  ) {
    for (
      let x = Math.floor(center - outer);
      x <= Math.ceil(center + outer);
      x++
    ) {
      const dx = x - center
      const dy = y - center
      const dist = Math.sqrt(dx * dx + dy * dy)
      if (dist > outer) continue

      const edgeNoise = ((x * 17 + y * 23 + size * 11) % 7) / 7
      let rgba: [number, number, number, number] | null = null

      if (dist > radius * 0.95) {
        rgba = [
          Math.max(0, mr - 72),
          Math.max(0, mg - 72),
          Math.max(0, mb - 72),
          1,
        ]
      } else if (dist > radius * 0.76) {
        rgba = [mr, mg, mb, 1]
      } else if (dist > radius * 0.48) {
        rgba = [lr, lg, lb, 1]
      } else {
        rgba = [255, 255, 255, 1]
      }

      if (dist > radius && dist <= outer) {
        const alpha = 0.28 + edgeNoise * 0.22
        rgba = [gr, gg, gb, alpha]
      }

      // Add an uneven corona so the ball reads like a living flame, not a flat disc.
      if (dist > radius * 0.62 && edgeNoise > 0.7) {
        rgba = [
          Math.min(255, lr + 18),
          Math.min(255, lg + 12),
          Math.min(255, lb + 6),
          1,
        ]
      }

      // Keep a bright top-left specular so the orb still reads as round.
      if (dx < -radius * 0.2 && dy < -radius * 0.2 && dist <= radius * 0.82) {
        rgba = [255, 255, 255, 1]
      }

      if (!rgba) continue
      const [rr, rg, rb, ra] = rgba
      ctx.fillStyle = `rgba(${rr},${rg},${rb},${ra})`
      ctx.fillRect(x, y, 1, 1)
    }
  }

  // Small ember sparks around the outside make the ball feel hot in motion.
  ctx.fillStyle = "rgba(255,255,255,0.85)"
  ctx.fillRect(center - 1, center - radius - 1, 2, 1)
  ctx.fillRect(center + radius - 1, center + 1, 2, 1)
  ctx.fillRect(center - radius, center + radius - 1, 1, 2)
  ctx.fillRect(center + 1, center - radius, 1, 2)
}

const drawBumperArtwork = (
  ctx: CanvasRenderingContext2D,
  size: number,
  effect: BumperEffectType,
): void => {
  const bumperDef = BUMPER_TYPES.find((b) => b.effect === effect)
  if (!bumperDef) return
  drawUnifiedBumper(ctx, size, bumperDef.color)
}

export class TextureManager {
  private scene: Phaser.Scene

  constructor(scene: Phaser.Scene) {
    this.scene = scene
  }

  public createProceduralTextures(): void {
    this.createProjectileTextures()
    this.createBumperTextures()
    this.createSlingshotTexture()
    this.createExpCoinTextures()
    this.createParticleTextures()
    this.createEquipmentTextures()
    this.createBackgroundNoiseTexture()
    this.generateBallTextures()
    this.createProceduralEnemyProjectiles()
    // Item icons are now shipped as pregenerated PNGs, but the old generator
    // remains here as a reference for future art iteration.
    void this.createItemIcons
  }

  private createProceduralEnemyProjectiles(): void {
    // ── Moai Neon Ring (32×32) — cyan pulsing digital ring ──
    if (!this.scene.textures.exists(TEX.PROJ_NEON_RING)) {
      const tex = this.scene.textures.createCanvas(TEX.PROJ_NEON_RING, 32, 32)
      if (tex) {
        const ctx = tex.context
        ctx.clearRect(0, 0, 32, 32)

        // Digital ring look: nested circles with varying alpha
        const color = "#38bdf8" // cyan-400
        ctx.strokeStyle = color
        ctx.lineWidth = 2
        ctx.beginPath()
        ctx.arc(16, 16, 12, 0, Math.PI * 2)
        ctx.stroke()

        ctx.strokeStyle = "rgba(56, 189, 248, 0.4)"
        ctx.lineWidth = 1
        ctx.beginPath()
        ctx.arc(16, 16, 14.5, 0, Math.PI * 2)
        ctx.stroke()

        ctx.strokeStyle = "rgba(56, 189, 248, 0.2)"
        ctx.lineWidth = 1
        ctx.beginPath()
        ctx.arc(16, 16, 10, 0, Math.PI * 2)
        ctx.stroke()

        // Hot specular pixel bits for that digital feel
        ctx.fillStyle = "#e0f2fe"
        ctx.fillRect(15, 4, 2, 1)
        ctx.fillRect(15, 27, 2, 1)
        ctx.fillRect(4, 15, 1, 2)
        ctx.fillRect(27, 15, 1, 2)

        tex.refresh()
      }
    }

    // ── Plasma Bolt (24×24) — magenta focused energy bolt ──
    if (!this.scene.textures.exists(TEX.PROJ_PLASMA_BOLT)) {
      const tex = this.scene.textures.createCanvas(TEX.PROJ_PLASMA_BOLT, 24, 24)
      if (tex) {
        const ctx = tex.context
        ctx.clearRect(0, 0, 24, 24)

        // Core glow
        const grad = ctx.createRadialGradient(12, 12, 0, 12, 12, 10)
        grad.addColorStop(0, "#f472b6") // pink-400
        grad.addColorStop(0.4, "rgba(244, 114, 182, 0.6)")
        grad.addColorStop(1, "rgba(244, 114, 182, 0)")
        ctx.fillStyle = grad
        ctx.beginPath()
        ctx.arc(12, 12, 10, 0, Math.PI * 2)
        ctx.fill()

        // Hot inner core
        ctx.fillStyle = "#fdf2f8"
        ctx.beginPath()
        ctx.arc(12, 12, 3.5, 0, Math.PI * 2)
        ctx.fill()

        // Energy streaks (cross shape)
        ctx.strokeStyle = "#fbcfe8"
        ctx.lineWidth = 1.5
        ctx.beginPath()
        ctx.moveTo(4, 12)
        ctx.lineTo(20, 12)
        ctx.stroke()
        ctx.beginPath()
        ctx.moveTo(12, 4)
        ctx.lineTo(12, 20)
        ctx.stroke()

        tex.refresh()
      }
    }
  }

  public createEnemyTextures(): void {
    // No procedural enemy textures are needed here.
  }

  // ── Combat ball textures (launcher-ball system) ──────────────────

  private generateBallTextures(): void {
    // Shared fireball — single combat ball texture for every hero.
    if (!this.scene.textures.exists(TEX.BALL_BASE)) {
      const tex = this.scene.textures.createCanvas(TEX.BALL_BASE, 24, 24)
      if (tex) {
        const ctx = tex.context
        drawFireballOrb(ctx, 24, 0xf97316, 0xffedd5, 0xfb923c)
        tex.refresh()
      }
    }
  }

  /**
   * Called from the scene's file-load-error handler.
   * Generates a bright magenta placeholder so the game never crashes
   * on a missing asset, and notifies listeners via EventBus.
   */
  public handleLoadError(file: { key: string; type: string }): void {
    const key = file.key
    const size = 64
    if (!key) {
      EventBus.emit(EVENT.ASSET_WARNING, {
        key: `${file.type}:missing-key`,
        type: file.type,
      })
      return
    }

    if (!this.scene.textures.exists(key)) {
      const g = this.scene.add.graphics()
      g.fillStyle(0xff00ff, 1)
      g.fillRect(0, 0, size, size)
      g.lineStyle(2, 0x000000, 1)
      g.strokeRect(0, 0, size, size)
      // Draw an X across the texture for visibility
      g.lineStyle(2, 0x000000, 1)
      g.beginPath()
      g.moveTo(0, 0)
      g.lineTo(size, size)
      g.moveTo(size, 0)
      g.lineTo(0, size)
      g.strokePath()
      g.generateTexture(key, size, size)
      g.destroy()
    }

    EventBus.emit(EVENT.ASSET_WARNING, { key, type: file.type })
  }

  // ── Pixel-art helper ─────────────────────────────────────────

  /** Draw pixels from a map string onto a canvas texture.
   *  Each char in the palette maps to a [color, alpha] pair.
   *  '.' = transparent. Lines are separated by '|'. */
  private pixelDraw(
    key: string,
    w: number,
    h: number,
    palette: Record<string, [number, number]>,
    map: string,
  ): void {
    if (this.scene.textures.exists(key)) return
    const tex = this.scene.textures.createCanvas(key, w, h)
    if (!tex) return
    const ctx = tex.context
    ctx.clearRect(0, 0, w, h)
    const rows = map.split("|")
    for (let y = 0; y < rows.length; y++) {
      for (let x = 0; x < rows[y].length; x++) {
        const ch = rows[y][x]
        if (ch === ".") continue
        const entry = palette[ch]
        if (!entry) continue
        const [color, alpha] = entry
        const r = (color >> 16) & 0xff
        const g = (color >> 8) & 0xff
        const b = color & 0xff
        ctx.fillStyle = `rgba(${r},${g},${b},${alpha})`
        ctx.fillRect(x, y, 1, 1)
      }
    }
    tex.refresh()
  }

  // ── Projectile textures ─────────────────────────────────────

  private createProjectileTextures(): void {
    // Remaining projectile textures are rendered as crisp pixel art below.
    // ── Bark wave (16×16) ──
    this.pixelDraw(
      TEX.PROJ_BARK,
      16,
      16,
      {
        D: [0xb8860b, 1], // dark gold outline
        G: [0xfacc15, 1], // gold main
        Y: [0xfde68a, 1], // bright yellow
        W: [0xfff8dc, 1], // white highlight
        g: [0xfacc15, 0.5], // gold glow
      },
      "................" +
        "|" +
        "......DDD......." +
        "|" +
        "....DDGGgD......" +
        "|" +
        "...DGYYW.gD....." +
        "|" +
        "..DGYW....gD...." +
        "|" +
        ".DGY.......gD..." +
        "|" +
        ".DGW........D..." +
        "|" +
        "DGY.........D..." +
        "|" +
        "DGY.........D..." +
        "|" +
        ".DGW........D..." +
        "|" +
        ".DGY.......gD..." +
        "|" +
        "..DGYW....gD...." +
        "|" +
        "...DGYYW.gD....." +
        "|" +
        "....DDGGgD......" +
        "|" +
        "......DDD......." +
        "|" +
        "................",
    )

    // ── Laser beam (20×16) — classic horizontal shmup bolt ──
    this.pixelDraw(
      TEX.PROJ_LASER,
      20,
      16,
      {
        D: [0x0f172a, 1], // dark outline
        B: [0x1d4ed8, 1], // deep blue body
        L: [0x38bdf8, 1], // bright blue edge
        C: [0x7dd3fc, 1], // cyan inner
        W: [0xf0f9ff, 1], // white-hot core
        g: [0x38bdf8, 0.25], // restrained aura
      },
      "...................." +
        "|" +
        "...................." +
        "|" +
        "...................." +
        "|" +
        ".................g.." +
        "|" +
        "...........gggLLDg.." +
        "|" +
        ".......gggLLLBBCCWBD" +
        "|" +
        "...ggLLLBBCCWWWWWWBD" +
        "|" +
        "ggLBBCCWWWWWWWWWWBD." +
        "|" +
        "ggLBBCCWWWWWWWWWWBD." +
        "|" +
        "...ggLLLBBCCWWWWWWBD" +
        "|" +
        ".......gggLLLBBCCWBD" +
        "|" +
        "...........gggLLDg.." +
        "|" +
        ".................g.." +
        "|" +
        "...................." +
        "|" +
        "...................." +
        "|" +
        "....................",
    )

    // ── Drool blob (16×16) — bubbly liquid projectile ──
    this.pixelDraw(
      TEX.PROJ_DROOL,
      16,
      16,
      {
        D: [0x1e3a5f, 1], // dark blue outline
        B: [0x3b82f6, 1], // blue body
        L: [0x60a5fa, 1], // light blue
        C: [0x93c5fd, 1], // cyan highlight
        W: [0xdbeafe, 1], // white specular
        b: [0x60a5fa, 0.4], // bubble trail
      },
      "................" +
        "|" +
        "................" +
        "|" +
        ".....DDDD......." +
        "|" +
        "...DDBBBBDD....." +
        "|" +
        "..DBLLLLLBBD...." +
        "|" +
        "..DBWCLLLBBD.b.." +
        "|" +
        ".DBLWCLLLLBBD..." +
        "|" +
        ".DBLLLLLLBBBD..." +
        "|" +
        ".DBLLLLLBBBD...." +
        "|" +
        "..DBBBBBBD.b...." +
        "|" +
        "..DDDDDDD......." +
        "|" +
        "....b..........." +
        "|" +
        "................" +
        "|" +
        ".......b........" +
        "|" +
        "................" +
        "|" +
        "................",
    )

    // ── Squeaky missile (24×16) — detailed rocket with fins ──
    this.pixelDraw(
      TEX.PROJ_MISSILE,
      24,
      16,
      {
        K: [0x374151, 1], // dark gray body
        G: [0x6b7280, 1], // gray body
        S: [0x9ca3af, 1], // silver highlight
        R: [0xef4444, 1], // red nose
        r: [0xb91c1c, 1], // dark red
        O: [0xf97316, 1], // orange flame
        Y: [0xfbbf24, 1], // yellow flame
        W: [0xfde68a, 1], // white-hot flame
        F: [0xd97706, 1], // fin dark
      },
      "........................" +
        "|" +
        "...............rR......." +
        "|" +
        "..F...KKKKKKKKRRR......." +
        "|" +
        ".FF.KKGGGGGGGSRRRR......" +
        "|" +
        ".FK.KGSSSSGGGGRRRR......" +
        "|" +
        "WOKKKGSSGGGGGGGRRR......" +
        "|" +
        "YOKKGGGGGGGGGGGRR......." +
        "|" +
        "OYKGGSSSSGGGGGRR........" +
        "|" +
        "OYKGGSSSSGGGGGRR........" +
        "|" +
        "YOKKGGGGGGGGGGGRR......." +
        "|" +
        "WOKKKGSSGGGGGGGRRR......" +
        "|" +
        ".FK.KGSSSSGGGGRRRR......" +
        "|" +
        ".FF.KKGGGGGGGSRRRR......" +
        "|" +
        "..F...KKKKKKKKRRR......." +
        "|" +
        "...............rR......." +
        "|" +
        "........................",
    )

    // ── Howl wave (40×24) — purple expanding shockwave ──
    if (!this.scene.textures.exists(TEX.PROJ_HOWL)) {
      const tex = this.scene.textures.createCanvas(TEX.PROJ_HOWL, 40, 24)
      if (tex) {
        const ctx = tex.context
        ctx.clearRect(0, 0, 40, 24)
        // Outer glow
        for (let ring = 3; ring >= 0; ring--) {
          const alpha = 0.08 + ring * 0.06
          ctx.fillStyle = `rgba(192,132,252,${alpha})`
          ctx.beginPath()
          ctx.ellipse(20, 12, 18 - ring, 10 - ring, 0, 0, Math.PI * 2)
          ctx.fill()
        }
        // Concentric pixel rings
        const drawPixelRing = (
          cx: number,
          cy: number,
          rx: number,
          ry: number,
          color: string,
        ) => {
          for (let a = 0; a < 360; a += 3) {
            const rad = (a * Math.PI) / 180
            const px = Math.round(cx + rx * Math.cos(rad))
            const py = Math.round(cy + ry * Math.sin(rad))
            ctx.fillStyle = color
            ctx.fillRect(px, py, 1, 1)
          }
        }
        drawPixelRing(20, 12, 16, 9, "rgba(233,213,255,0.9)")
        drawPixelRing(20, 12, 13, 7, "rgba(192,132,252,0.95)")
        drawPixelRing(20, 12, 10, 5, "rgba(168,85,247,0.8)")
        // Center energy lines
        ctx.fillStyle = "rgba(255,255,255,0.85)"
        for (let x = 2; x < 12; x += 2) {
          ctx.fillRect(x, 11, 2, 1)
          ctx.fillRect(x, 13, 2, 1)
        }
        tex.refresh()
      }
    }

    // ── Boomerang (32×24) — bone-shaped boomerang ──
    this.pixelDraw(
      TEX.PROJ_BOOMERANG,
      32,
      24,
      {
        D: [0x92400e, 1], // dark brown outline
        B: [0xf59e0b, 1], // amber body
        Y: [0xfde68a, 1], // yellow highlight
        W: [0xfef3c7, 1], // white specular
        b: [0xd97706, 1], // mid-brown shading
      },
      "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        ".................DDD............" +
        "|" +
        "...............DDBBBDD.........." +
        "|" +
        "..............DBYYYBBD.........." +
        "|" +
        ".............DBYWYBBBD.........." +
        "|" +
        "............DBYBBBBD............" +
        "|" +
        "..........DDBBBBD..............." +
        "|" +
        ".........DBBBbD................." +
        "|" +
        "........DBYBbD.................." +
        "|" +
        "........DBYBbD.................." +
        "|" +
        ".........DBBBbD................." +
        "|" +
        "..........DDBBBBD..............." +
        "|" +
        "............DBYBBBBD............" +
        "|" +
        ".............DBYWYBBBD.........." +
        "|" +
        "..............DBYYYBBD.........." +
        "|" +
        "...............DDBBBDD.........." +
        "|" +
        ".................DDD............" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................",
    )

    // ── Treat mine (24×24) — spiky danger orb ──
    this.pixelDraw(
      TEX.PROJ_MINE,
      24,
      24,
      {
        K: [0x78350f, 1], // dark brown outline
        O: [0xfb923c, 1], // orange body
        Y: [0xfcd34d, 1], // yellow core
        W: [0xfef3c7, 1], // white hot center
        R: [0xea580c, 1], // red-orange shadow
        S: [0xfde68a, 1], // spike tip
      },
      "........................" +
        "|" +
        "...........SK..........." +
        "|" +
        "...........SK..........." +
        "|" +
        ".....S....SKKS....S....." +
        "|" +
        "......SK.KOOKK.KS......" +
        "|" +
        ".......KKOOOOOOKK......." +
        "|" +
        "......KOORROOROOOK......" +
        "|" +
        ".....KOORROOYOROOOK....." +
        "|" +
        "....KOOROOYWYOOROOK....." +
        "|" +
        ".SKKOOROOYWWYOOROOKKS..." +
        "|" +
        ".SKKOOROOYWYOOROOOKKS..." +
        "|" +
        "....KOORROOYOROOOK......" +
        "|" +
        ".....KOORROOROOOK......." +
        "|" +
        "......KOOOOOOOOOK......." +
        "|" +
        ".......KKOOOOOOKK......." +
        "|" +
        "......SK.KOOKK.KS......" +
        "|" +
        ".....S....SKKS....S....." +
        "|" +
        "...........SK..........." +
        "|" +
        "...........SK..........." +
        "|" +
        "........................" +
        "|" +
        "........................" +
        "|" +
        "........................" +
        "|" +
        "........................" +
        "|" +
        "........................",
    )

    // ── Enemy projectile (16×16) — menacing red energy orb ──
    this.pixelDraw(
      TEX.PROJ_ENEMY,
      16,
      16,
      {
        K: [0x7f1d1d, 1], // dark red outline
        R: [0xef4444, 1], // red body
        O: [0xf97316, 1], // orange inner
        Y: [0xfbbf24, 1], // yellow core
        W: [0xffffff, 1], // white hot center
        g: [0xef4444, 0.3], // red glow
      },
      "................" +
        "|" +
        "......gg........" +
        "|" +
        ".....gKKg......." +
        "|" +
        "....gKRRKg......" +
        "|" +
        "...gKRRRRKg....." +
        "|" +
        "...KRROORRKK...." +
        "|" +
        "..KRROYYOR.K...." +
        "|" +
        "..KROYWWYORK...." +
        "|" +
        "..KROYWWYORK...." +
        "|" +
        "..KRROYYOR.K...." +
        "|" +
        "...KRROORRKK...." +
        "|" +
        "...gKRRRRKg....." +
        "|" +
        "....gKRRKg......" +
        "|" +
        ".....gKKg......." +
        "|" +
        "......gg........" +
        "|" +
        "................",
    )

    // ── Howl wave (32×32) — crisp purple shockwave ──
    this.pixelDraw(
      TEX.PROJ_HOWL,
      32,
      32,
      {
        P: [0xc084fc, 1],
        L: [0xe9d5ff, 1],
        D: [0x7c3aed, 1],
        W: [0xffffff, 1],
      },
      "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        ".............DDDD..............." +
        "|" +
        "..........DDPPPPPPDD............" +
        "|" +
        "........DPPLLLLLLLPPD..........." +
        "|" +
        ".......DPLLLWWWWLLLPPD.........." +
        "|" +
        "......DPLLWWWWWWWWLLPPD........." +
        "|" +
        "......DPLLWW....WWLLPPD........." +
        "|" +
        ".....DPPLWW......WWLPPD........." +
        "|" +
        ".....DPPLWW......WWLPPD........." +
        "|" +
        "......DPLLWW....WWLLPPD........." +
        "|" +
        "......DPLLWWWWWWWWLLPPD........." +
        "|" +
        ".......DPLLLWWWWLLLPPD.........." +
        "|" +
        "........DPPLLLLLLLPPD..........." +
        "|" +
        "..........DDPPPPPPDD............" +
        "|" +
        ".............DDDD..............." +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................",
    )
  }

  // ── Bumper textures ─────────────────────────────────────────

  private createBumperTextures(): void {
    for (const bumperType of BUMPER_TYPES) {
      if (this.scene.textures.exists(bumperType.textureKey)) continue
      const r = bumperType.radius
      const w = r * 2
      const tex = this.scene.textures.createCanvas(bumperType.textureKey, w, w)
      if (!tex) continue
      const ctx = tex.context
      ctx.clearRect(0, 0, w, w)
      ctx.imageSmoothingEnabled = false

      drawBumperArtwork(ctx, w, bumperType.effect)

      tex.refresh()
    }
  }

  // ── Slingshot deflector texture ──────────────────────────

  /**
   * Draws a right-pointing neon chevron/boomerang — the slingshot deflector.
   * Inspired by classic pinball slingshot shapes and Sonic Superstars carnival
   * flipper deflectors. Single texture; sprite is rotated at spawn to face
   * the kick direction.
   */
  private createSlingshotTexture(): void {
    const key = TEX.BUMPER_SLINGSHOT
    if (this.scene.textures.exists(key)) return
    const w = 26
    const h = 15
    const tex = this.scene.textures.createCanvas(key, w, h)
    if (!tex) return
    const ctx = tex.context
    ctx.clearRect(0, 0, w, h)
    ctx.imageSmoothingEnabled = false

    // Chunky, SNES-style chevron: flat fill, hard outline, one hot tip accent.
    ctx.fillStyle = "rgba(33,53,10,1)"
    ctx.beginPath()
    ctx.moveTo(24, 7)
    ctx.lineTo(16, 2)
    ctx.lineTo(4, 4)
    ctx.lineTo(8, 7)
    ctx.lineTo(4, 11)
    ctx.lineTo(16, 13)
    ctx.closePath()
    ctx.fill()

    ctx.fillStyle = "rgba(101,163,13,1)"
    ctx.beginPath()
    ctx.moveTo(23, 7)
    ctx.lineTo(16, 3)
    ctx.lineTo(6, 5)
    ctx.lineTo(10, 7)
    ctx.lineTo(6, 10)
    ctx.lineTo(16, 12)
    ctx.closePath()
    ctx.fill()

    ctx.fillStyle = "rgba(190,242,100,1)"
    ctx.fillRect(22, 6, 2, 2)
    ctx.fillRect(20, 7, 2, 1)
    ctx.fillRect(21, 5, 1, 1)

    ctx.fillStyle = "rgba(217,249,157,1)"
    ctx.fillRect(15, 4, 2, 1)
    ctx.fillRect(12, 6, 2, 1)
    ctx.fillRect(12, 9, 2, 1)
    ctx.fillRect(15, 11, 1, 1)

    ctx.fillStyle = "rgba(0,0,0,0.45)"
    ctx.beginPath()
    ctx.moveTo(9, 7)
    ctx.lineTo(12, 6)
    ctx.lineTo(12, 9)
    ctx.closePath()
    ctx.fill()

    tex.refresh()
  }

  // ── Experience coin textures (3 tiers — gold coins) ──────

  private createExpCoinTextures(): void {
    // Small coin — 8×10 gold coin
    this.pixelDraw(
      TEX.COIN_S,
      8,
      10,
      {
        D: [0x92400e, 1], // dark gold outline
        G: [0xd97706, 1], // gold body
        L: [0xfbbf24, 1], // light gold highlight
        W: [0xfef3c7, 1], // white-gold specular
        g: [0xd97706, 0.3], // glow
      },
      "...gg..." +
        "|" +
        "...Dg..." +
        "|" +
        "..DWD..." +
        "|" +
        ".DLGLD.." +
        "|" +
        ".DGLGD.." +
        "|" +
        "DGGGGGD." +
        "|" +
        ".DGGGGD." +
        "|" +
        "..DGGD.." +
        "|" +
        "...DD..." +
        "|" +
        "........",
    )

    // Medium coin — 12×14 gold coin
    this.pixelDraw(
      TEX.COIN_M,
      12,
      14,
      {
        D: [0x92400e, 1], // dark gold outline
        G: [0xd97706, 1], // gold body
        L: [0xfbbf24, 1], // light gold highlight
        W: [0xfef3c7, 1], // white-gold specular
        H: [0xffffff, 0.9], // hot specular
        g: [0xd97706, 0.3], // glow
      },
      "....gg......" +
        "|" +
        ".....Dg....." +
        "|" +
        "....DHD....." +
        "|" +
        "...DWLD....." +
        "|" +
        "..DWLGLD...." +
        "|" +
        "..DLGLGD...." +
        "|" +
        ".DLGGGGGD..." +
        "|" +
        ".DGGGGGD...." +
        "|" +
        "DGGGGGGD...." +
        "|" +
        ".DGGGGGD...." +
        "|" +
        "..DGGGGD...." +
        "|" +
        "...DGGD....." +
        "|" +
        "....DD......" +
        "|" +
        "............",
    )

    // Large coin — 16×18 gold coin
    this.pixelDraw(
      TEX.COIN_L,
      16,
      18,
      {
        D: [0x92400e, 1], // dark gold outline
        G: [0xd97706, 1], // gold body
        L: [0xfbbf24, 1], // light gold highlight
        W: [0xfef3c7, 1], // white-gold specular
        H: [0xffffff, 0.9], // hot specular
        g: [0xd97706, 0.3], // glow
      },
      "......gg........" +
        "|" +
        ".......Dg......." +
        "|" +
        "......DHD......." +
        "|" +
        ".....DHWD......." +
        "|" +
        "....DWWLD......." +
        "|" +
        "...DWLLGLD......" +
        "|" +
        "...DLGLGGD......" +
        "|" +
        "..DLGGGGGGD....." +
        "|" +
        "..DGGGGGGGD....." +
        "|" +
        ".DGGGGGGGGD....." +
        "|" +
        ".DGGGGGGGD......" +
        "|" +
        "..DGGGGGGD......" +
        "|" +
        "..DGGGGGD......." +
        "|" +
        "...DGGGGD......." +
        "|" +
        "....DGGD........" +
        "|" +
        ".....DD........." +
        "|" +
        "................" +
        "|" +
        "................",
    )
  }

  // ── Particle textures ───────────────────────────────────────

  private createParticleTextures(): void {
    // ── Particle (8×8) — soft energy orb with core ──
    this.pixelDraw(
      TEX.PARTICLE,
      8,
      8,
      {
        W: [0xffffff, 1],
        L: [0xdbeafe, 0.9],
        G: [0xbfdbfe, 0.6],
        g: [0x93c5fd, 0.3],
      },
      "..gGGg.." +
        "|" +
        ".gGLLGg." +
        "|" +
        "gGLWWLGg" +
        "|" +
        "GLWWWWLg" +
        "|" +
        "GLWWWWLg" +
        "|" +
        "gGLWWLGg" +
        "|" +
        ".gGLLGg." +
        "|" +
        "..gGGg..",
    )

    // ── Spark (8×8) — 4-point star burst ──
    this.pixelDraw(
      TEX.SPARK,
      8,
      8,
      {
        W: [0xffffff, 1],
        Y: [0xfbbf24, 1],
        G: [0xfde68a, 1],
        g: [0xfbbf24, 0.5],
      },
      "...Yg..." +
        "|" +
        "...YG..." +
        "|" +
        "...YW..." +
        "|" +
        "gGYWWYGg" +
        "|" +
        "gGYWWYGg" +
        "|" +
        "...YW..." +
        "|" +
        "...YG..." +
        "|" +
        "...Yg...",
    )

    // ── Fire (8×8) — flame pixel with gradient ──
    this.pixelDraw(
      TEX.FIRE,
      8,
      8,
      {
        R: [0xdc2626, 1], // dark red base
        O: [0xf97316, 1], // orange mid
        Y: [0xfbbf24, 1], // yellow hot
        W: [0xfef3c7, 1], // white-hot core
        r: [0xef4444, 0.5], // glow
      },
      "..rOr..." +
        "|" +
        ".rOYOr.." +
        "|" +
        ".OYWYO.." +
        "|" +
        "ROYWWYOR" +
        "|" +
        "ROYWWYOR" +
        "|" +
        ".OYWYYO." +
        "|" +
        ".rOYYOr." +
        "|" +
        "..rRRr..",
    )

    // ── Flash (8×8) — diamond muzzle flash ──
    this.pixelDraw(
      TEX.FLASH,
      8,
      8,
      {
        W: [0xffffff, 1],
        Y: [0xfef3c7, 1],
        G: [0xfde68a, 0.8],
        g: [0xfbbf24, 0.4],
      },
      "...Wg..." +
        "|" +
        "..gWYg.." +
        "|" +
        ".gYWWYg." +
        "|" +
        "WYWWWWYW" +
        "|" +
        "WYWWWWYW" +
        "|" +
        ".gYWWYg." +
        "|" +
        "..gWYg.." +
        "|" +
        "...Wg...",
    )

    // ── Smoke (16×16) — pixel cloud puff ──
    this.pixelDraw(
      TEX.SMOKE,
      16,
      16,
      {
        D: [0x64748b, 0.5], // dark gray
        M: [0x94a3b8, 0.45], // mid gray
        L: [0xcbd5e1, 0.4], // light gray
        W: [0xe2e8f0, 0.35], // white-gray
      },
      "................" +
        "|" +
        "......MMMM......" +
        "|" +
        "....MMLLLLMM...." +
        "|" +
        "...MLLWWWLLM...." +
        "|" +
        "..MLLWWWWLLMM..." +
        "|" +
        ".MLLWWWWWLLLLM.." +
        "|" +
        ".MLWWWWWWWWLLM.." +
        "|" +
        "MLLWWWWWWWWLLM.." +
        "|" +
        "MLLWWWWWWWWWLM.." +
        "|" +
        "MLLLWWWWWWWLLM.." +
        "|" +
        ".MLLLLWWWLLLM..." +
        "|" +
        ".MMLLLLLLLLMM..." +
        "|" +
        "..MMMLLLMMM....." +
        "|" +
        "....DMMMMD......" +
        "|" +
        "......DD........" +
        "|" +
        "................",
    )
  }

  // ── Item & upgrade icons (32×32) ─────────────────────────────

  private createItemIcons(): void {
    this.createLauncherIcons()
    this.createUpgradeCategoryIcons()
  }

  private createLauncherIcons(): void {
    // barkBlaster: golden sonic arc
    this.pixelDraw(
      TEX.ICON_LAUNCHER_BARK_BLASTER,
      32,
      32,
      {
        G: [0xfacc15, 1],
        Y: [0xfde68a, 1],
        D: [0xb8860b, 1],
        W: [0xffffff, 1],
      },
      "................................" +
        "|" +
        "................................" +
        "|" +
        "..........DDDD.................." +
        "|" +
        "........DDGGYYD................." +
        "|" +
        ".......DGYYW..D................." +
        "|" +
        "......DGY......D................" +
        "|" +
        ".....DGW........D..............." +
        "|" +
        ".....DG..........D.............." +
        "|" +
        "....DGY..........D.............." +
        "|" +
        "....DG............D............." +
        "|" +
        "....DG............D............." +
        "|" +
        "....DG............D............." +
        "|" +
        "....DG............D............." +
        "|" +
        "....DGY..........D.............." +
        "|" +
        ".....DG..........D.............." +
        "|" +
        ".....DGW........D..............." +
        "|" +
        "......DGY......D................" +
        "|" +
        ".......DGYYW..D................." +
        "|" +
        "........DDGGYYD................." +
        "|" +
        "..........DDDD.................." +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................",
    )

    // treatLauncher: fan of 3 small circles (treats)
    this.pixelDraw(
      TEX.ICON_LAUNCHER_TREAT_LAUNCHER,
      32,
      32,
      {
        O: [0xf59e0b, 1],
        Y: [0xfde68a, 1],
        D: [0x92400e, 1],
      },
      "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        ".............DDD................" +
        "|" +
        "............DOOOD..............." +
        "|" +
        "............DOYOD..............." +
        "|" +
        "............DOOOD......DDD....." +
        "|" +
        ".............DDD......DOOOD...." +
        "|" +
        ".....................DOYOD....." +
        "|" +
        ".....................DOOOD....." +
        "|" +
        "......................DDD......" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "...........DDD.................." +
        "|" +
        "..........DOOOD................." +
        "|" +
        "..........DOYOD................." +
        "|" +
        "..........DOOOD................." +
        "|" +
        "...........DDD.................." +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................",
    )

    // laserPointer: red crosshair
    this.pixelDraw(
      TEX.ICON_LAUNCHER_LASER_POINTER,
      32,
      32,
      {
        R: [0xef4444, 1],
        D: [0x991b1b, 1],
        W: [0xffffff, 1],
      },
      "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "...............R................" +
        "|" +
        "...............R................" +
        "|" +
        "...............R................" +
        "|" +
        "...............R................" +
        "|" +
        "...............R................" +
        "|" +
        "..........DDDDDDDDD............" +
        "|" +
        ".........D.........D..........." +
        "|" +
        "........D...........D.........." +
        "|" +
        ".......D.............D........." +
        "|" +
        ".......D......W......D........." +
        "|" +
        "..RRRRD......WRW.....DRRRR....." +
        "|" +
        ".......D......W......D........." +
        "|" +
        ".......D.............D........." +
        "|" +
        "........D...........D.........." +
        "|" +
        ".........D.........D..........." +
        "|" +
        "..........DDDDDDDDD............" +
        "|" +
        "...............R................" +
        "|" +
        "...............R................" +
        "|" +
        "...............R................" +
        "|" +
        "...............R................" +
        "|" +
        "...............R................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................",
    )

    // tennisBarrage: green ball
    this.pixelDraw(
      TEX.ICON_LAUNCHER_TENNIS_BARRAGE,
      32,
      32,
      {
        G: [0x84cc16, 1],
        L: [0xbef264, 1],
        D: [0x365314, 1],
        W: [0xffffff, 1],
      },
      "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "..........DDDDDDDD.............." +
        "|" +
        "........DDGGGGGGGGDD............" +
        "|" +
        ".......DGGLLLGGGGGGGD..........." +
        "|" +
        "......DGWLLGGGGGGGGD............" +
        "|" +
        "......DGLLLGGGGGGGD............." +
        "|" +
        ".....DGGGGGDDDDDDGD............." +
        "|" +
        ".....DGGGDD....DDGGD............" +
        "|" +
        ".....DGGD........DGGD..........." +
        "|" +
        ".....DGGGDD....DDGGD............" +
        "|" +
        ".....DGGGGGDDDDDDGD............." +
        "|" +
        "......DGGGGGGGGGGGD............." +
        "|" +
        "......DGGGGGGGGGGD.............." +
        "|" +
        ".......DGGGGGGGGD..............." +
        "|" +
        "........DDGGGGDD................" +
        "|" +
        "..........DDDDDD................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................",
    )

    // droolCannon: cyan dripping blob
    this.pixelDraw(
      TEX.ICON_LAUNCHER_DROOL_CANNON,
      32,
      32,
      {
        C: [0x06b6d4, 1],
        L: [0x67e8f9, 1],
        D: [0x0e4d5c, 1],
        W: [0xffffff, 1],
      },
      "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "..........DDDDDDD..............." +
        "|" +
        "........DDCCCCCCDD.............." +
        "|" +
        ".......DCCLLLLCCCD.............." +
        "|" +
        "......DCLWWLCCCCCCD............." +
        "|" +
        "......DCLLCCCCCCCD.............." +
        "|" +
        "......DCCCCCCCCCD..............." +
        "|" +
        ".......DDCCCCCDD................" +
        "|" +
        "........DDDDDDD................." +
        "|" +
        "..........D...D................." +
        "|" +
        ".........DC...DC................" +
        "|" +
        ".........DC....D................" +
        "|" +
        "..........D......................" +
        "|" +
        ".........DC....................." +
        "|" +
        "..........D....................." +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................",
    )

    // squeakyMissile: orange rocket
    this.pixelDraw(
      TEX.ICON_LAUNCHER_SQUEAKY_MISSILE,
      32,
      32,
      {
        O: [0xf97316, 1],
        R: [0xef4444, 1],
        G: [0x6b7280, 1],
        D: [0x374151, 1],
        Y: [0xfbbf24, 1],
        W: [0xffffff, 1],
      },
      "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "...............RR..............." +
        "|" +
        "..D........DDDRRR.............." +
        "|" +
        ".DD.....DDGGGGRRRR.............." +
        "|" +
        ".DD...DDGGGWGGRRRRR............." +
        "|" +
        "YDDDDGGGGWGGGGGRRR.............." +
        "|" +
        "ODDDDGGGGGGGGGRR................" +
        "|" +
        "YDDDDGGGGWGGGGGRRR.............." +
        "|" +
        ".DD...DDGGGWGGRRRRR............." +
        "|" +
        ".DD.....DDGGGGRRRR.............." +
        "|" +
        "..D........DDDRRR.............." +
        "|" +
        "...............RR..............." +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................",
    )

    // bouncingBall: yellow bouncing sphere
    this.pixelDraw(
      TEX.ICON_LAUNCHER_BOUNCING_BALL,
      32,
      32,
      {
        Y: [0xeab308, 1],
        L: [0xfde68a, 1],
        D: [0x854d0e, 1],
        W: [0xffffff, 1],
      },
      "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "..........DDDDDDDD.............." +
        "|" +
        "........DDYYYYYYYYDD............" +
        "|" +
        ".......DYYYLLLYYYYYYYD..........." +
        "|" +
        "......DYWWLLYYYYYYYYYYD.........." +
        "|" +
        "......DYLLYYYYYYYYYYYD..........." +
        "|" +
        ".....DYYYYYYYYYYYYYYD............" +
        "|" +
        ".....DYYYYYYYYYYYYYYD............" +
        "|" +
        ".....DYYYYYYYYYYYYYYD............" +
        "|" +
        ".....DYYYYYYYYYYYYYYD............" +
        "|" +
        "......DYYYYYYYYYYYYYYYD.........." +
        "|" +
        "......DYYYYYYYYYYYYYYYD.........." +
        "|" +
        ".......DYYYYYYYYYYYD............." +
        "|" +
        "........DDYYYYYYYYDD............" +
        "|" +
        "..........DDDDDDDD.............." +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................",
    )

    // puppyPack: purple paw print
    this.pixelDraw(
      TEX.ICON_LAUNCHER_PUPPY_PACK,
      32,
      32,
      {
        P: [0xa855f7, 1],
        L: [0xc084fc, 1],
        D: [0x581c87, 1],
      },
      "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        ".......DDD..........DDD........." +
        "|" +
        "......DPPD.........DPPD........." +
        "|" +
        "......DLPD.........DLPD........." +
        "|" +
        ".......DDD..........DDD........." +
        "|" +
        "................................" +
        "|" +
        "....DDD..............DDD........" +
        "|" +
        "...DPPD.............DPPD........" +
        "|" +
        "...DLPD.............DLPD........" +
        "|" +
        "....DDD..............DDD........" +
        "|" +
        "................................" +
        "|" +
        "..........DDDDDDD..............." +
        "|" +
        ".........DPPPPPPPD.............." +
        "|" +
        "........DPPLLLLLPPD............." +
        "|" +
        "........DPLLLLLLLPD............." +
        "|" +
        "........DPPPPPPPPPD............." +
        "|" +
        ".........DPPPPPPD..............." +
        "|" +
        "..........DDDDDD................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................",
    )

    // boneBarrage: white bone shape
    this.pixelDraw(
      TEX.ICON_LAUNCHER_BONE_BARRAGE,
      32,
      32,
      {
        W: [0xfafafa, 1],
        L: [0xe5e7eb, 1],
        D: [0x9ca3af, 1],
      },
      "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "......DDD...........DDD........." +
        "|" +
        ".....DWWWD.........DWWWD........" +
        "|" +
        ".....DWLWD.........DWLWD........" +
        "|" +
        "......DWWD.........DWWD........." +
        "|" +
        ".......DWD.........DWD.........." +
        "|" +
        "........DWWWWWWWWWWD............" +
        "|" +
        "........DWLLLLLLLLWD............" +
        "|" +
        "........DWWWWWWWWWWD............" +
        "|" +
        ".......DWD.........DWD.........." +
        "|" +
        "......DWWD.........DWWD........." +
        "|" +
        ".....DWLWD.........DWLWD........" +
        "|" +
        ".....DWWWD.........DWWWD........" +
        "|" +
        "......DDD...........DDD........." +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................",
    )

    // howl: purple expanding wave arcs
    this.pixelDraw(
      TEX.ICON_LAUNCHER_HOWL,
      32,
      32,
      {
        P: [0xc084fc, 1],
        D: [0x7c3aed, 1],
        L: [0xe9d5ff, 1],
      },
      "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "..P.....D.....L................." +
        "|" +
        "..P....D.....L.................." +
        "|" +
        "..P...D.....L..................." +
        "|" +
        "..P..D.....L...................." +
        "|" +
        "..P.D.....L....................." +
        "|" +
        "..PD.....L......................" +
        "|" +
        "..PD....L......................." +
        "|" +
        "..PD....L......................." +
        "|" +
        "..PD.....L......................" +
        "|" +
        "..P.D.....L....................." +
        "|" +
        "..P..D.....L...................." +
        "|" +
        "..P...D.....L..................." +
        "|" +
        "..P....D.....L.................." +
        "|" +
        "..P.....D.....L................." +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................",
    )

    // boomerang: pink curved return shape
    this.pixelDraw(
      TEX.ICON_LAUNCHER_BOOMERANG,
      32,
      32,
      {
        P: [0xf472b6, 1],
        D: [0xbe185d, 1],
        L: [0xfbcfe8, 1],
      },
      "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "...............DDD.............." +
        "|" +
        ".............DDPPPDD............" +
        "|" +
        "............DPLLPPPD............" +
        "|" +
        "...........DPLPPPPD............." +
        "|" +
        "..........DPPPPPD..............." +
        "|" +
        ".........DPPPPD................." +
        "|" +
        "........DPPPD..................." +
        "|" +
        ".......DPPD....................." +
        "|" +
        "........DPPPD..................." +
        "|" +
        ".........DPPPPD................." +
        "|" +
        "..........DPPPPPD..............." +
        "|" +
        "...........DPLPPPPD............." +
        "|" +
        "............DPLLPPPD............" +
        "|" +
        ".............DDPPPDD............" +
        "|" +
        "...............DDD.............." +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................",
    )

    // treatMine: orange spiky circle
    this.pixelDraw(
      TEX.ICON_LAUNCHER_TREAT_MINE,
      32,
      32,
      {
        O: [0xfb923c, 1],
        Y: [0xfcd34d, 1],
        D: [0x78350f, 1],
        S: [0xfde68a, 1],
      },
      "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "..............SD................" +
        "|" +
        "..............SD................" +
        "|" +
        "......S......SDDS......S........" +
        "|" +
        ".......SD...DOOD...DS.........." +
        "|" +
        "........DDDOOOOODD.............." +
        "|" +
        ".......DOOOYOOOOOOD............." +
        "|" +
        "......DOOYOOYOOOOOD............." +
        "|" +
        ".SDDDDOOOYYYOOOOODDDDS.........." +
        "|" +
        ".SDDDDOOOOYOOOOOOODDDDS........." +
        "|" +
        "......DOOOOOOOOOOD.............." +
        "|" +
        ".......DOOOOOOOOOD.............." +
        "|" +
        "........DDDOOOOODD.............." +
        "|" +
        ".......SD...DOOD...DS.........." +
        "|" +
        "......S......SDDS......S........" +
        "|" +
        "..............SD................" +
        "|" +
        "..............SD................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................",
    )

    // plasmaRail: purple charged beam line
    this.pixelDraw(
      TEX.ICON_LAUNCHER_PLASMA_RAIL,
      32,
      32,
      {
        P: [0xa855f7, 1],
        L: [0xc084fc, 1],
        W: [0xe9d5ff, 1],
        D: [0x581c87, 1],
        g: [0xa855f7, 0.4],
      },
      "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "..gggggggggggggggggggggggg......" +
        "|" +
        ".gDDPPPPPPPPPPPPPPPPPPDDg......." +
        "|" +
        "gDPLLLWWWWWWWWWWWLLLPPDg........" +
        "|" +
        "DPWWWWWWWWWWWWWWWWWWWWPD........." +
        "|" +
        "DPWWWWWWWWWWWWWWWWWWWWPD........." +
        "|" +
        "gDPLLLWWWWWWWWWWWLLLPPDg........" +
        "|" +
        ".gDDPPPPPPPPPPPPPPPPPPDDg......." +
        "|" +
        "..gggggggggggggggggggggggg......" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................",
    )
  }

  private createUpgradeCategoryIcons(): void {
    // hp: red heart
    this.pixelDraw(
      TEX.ICON_UPGRADE_HP,
      32,
      32,
      {
        R: [0xef4444, 1],
        D: [0x991b1b, 1],
        L: [0xfca5a5, 1],
        W: [0xffffff, 1],
      },
      "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "......DDDDD.....DDDDD.........." +
        "|" +
        ".....DRRRRRD...DRRRRRD........." +
        "|" +
        "....DRRLRRRD.DRRRRRRRRD........" +
        "|" +
        "....DRWLRRRRDRRRRRRRRRD........" +
        "|" +
        "....DRRLRRRRRRRRRRRRRD........." +
        "|" +
        ".....DRRRRRRRRRRRRRRRD........." +
        "|" +
        ".....DRRRRRRRRRRRRRRD.........." +
        "|" +
        "......DRRRRRRRRRRRRRD.........." +
        "|" +
        ".......DRRRRRRRRRRRD..........." +
        "|" +
        "........DRRRRRRRRRD............" +
        "|" +
        ".........DRRRRRRRD............." +
        "|" +
        "..........DRRRRRD.............." +
        "|" +
        "...........DRRRD..............." +
        "|" +
        "............DRD................" +
        "|" +
        ".............D................." +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................",
    )

    // damage: orange fang/tooth
    this.pixelDraw(
      TEX.ICON_UPGRADE_DAMAGE,
      32,
      32,
      {
        O: [0xf97316, 1],
        D: [0x9a3412, 1],
        Y: [0xfde68a, 1],
        W: [0xffffff, 1],
      },
      "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "........DDDDDDDDDDD............" +
        "|" +
        "........DOOOOOOOOOD............." +
        "|" +
        ".........DOOOOOOOOD............." +
        "|" +
        ".........DOOYOOOOOD............." +
        "|" +
        "..........DOWOOOOD.............." +
        "|" +
        "..........DOYOOOOD.............." +
        "|" +
        "...........DOOOOOD.............." +
        "|" +
        "...........DOOOOOD.............." +
        "|" +
        "............DOOOOD.............." +
        "|" +
        "............DOOOOD.............." +
        "|" +
        ".............DOOOD.............." +
        "|" +
        ".............DOOOD.............." +
        "|" +
        "..............DOOD.............." +
        "|" +
        "..............DOD..............." +
        "|" +
        "...............DD..............." +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................",
    )

    // speed: yellow lightning bolt
    this.pixelDraw(
      TEX.ICON_UPGRADE_SPEED,
      32,
      32,
      {
        Y: [0xfbbf24, 1],
        D: [0x854d0e, 1],
        W: [0xffffff, 1],
      },
      "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        ".............DDDD..............." +
        "|" +
        "............DYYYD..............." +
        "|" +
        "...........DYYYD................" +
        "|" +
        "..........DYYYD................." +
        "|" +
        ".........DYYYD.................." +
        "|" +
        "........DYYYD..................." +
        "|" +
        ".......DYYYYYYYYD..............." +
        "|" +
        "........DDDDDYYD................" +
        "|" +
        "...............DYYD.............." +
        "|" +
        "..............DYYD.............." +
        "|" +
        ".............DYYD..............." +
        "|" +
        "............DYYD................" +
        "|" +
        "...........DYYD................." +
        "|" +
        "..........DYYYD................." +
        "|" +
        "..........DDDD.................." +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................",
    )

    // defense: blue shield
    this.pixelDraw(
      TEX.ICON_UPGRADE_DEFENSE,
      32,
      32,
      {
        B: [0x3b82f6, 1],
        D: [0x1e40af, 1],
        L: [0x93c5fd, 1],
        W: [0xffffff, 1],
      },
      "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "......DDDDDDDDDDDDD............" +
        "|" +
        ".....DBBBBBBBBBBBBBD............" +
        "|" +
        ".....DBLLLBBBBBBBBD............." +
        "|" +
        ".....DBWLBBBBBBBBBD............." +
        "|" +
        "......DBBBBBBBBBBD.............." +
        "|" +
        "......DBBBBBBBBBBD.............." +
        "|" +
        ".......DBBBBBBBBBD.............." +
        "|" +
        ".......DBBBBBBBBBD.............." +
        "|" +
        "........DBBBBBBD................" +
        "|" +
        "........DBBBBBBD................" +
        "|" +
        ".........DBBBBD................." +
        "|" +
        ".........DBBBBD................." +
        "|" +
        "..........DBBBD................." +
        "|" +
        "..........DBBD.................." +
        "|" +
        "...........DDD.................." +
        "|" +
        "............D..................." +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................",
    )

    // bumper: pink bumper circle
    this.pixelDraw(
      TEX.ICON_UPGRADE_BUMPER,
      32,
      32,
      {
        P: [0xf472b6, 1],
        D: [0xbe185d, 1],
        L: [0xfbcfe8, 1],
        W: [0xffffff, 1],
      },
      "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "..........DDDDDDD..............." +
        "|" +
        "........DDPPPPPPPDD............." +
        "|" +
        ".......DPPLLLPPPPPPD............" +
        "|" +
        "......DPPWLPPPPPPPPPD..........." +
        "|" +
        "......DPPLPPPPPPPPPPD..........." +
        "|" +
        ".....DPPPPPPPPPPPPPPD..........." +
        "|" +
        ".....DPPPPPPWPPPPPPD............" +
        "|" +
        ".....DPPPPPPPPPPPPD............." +
        "|" +
        ".....DPPPPPPPPPPPD.............." +
        "|" +
        "......DPPPPPPPPPPD.............." +
        "|" +
        "......DPPPPPPPPPPD.............." +
        "|" +
        ".......DPPPPPPPD................" +
        "|" +
        "........DDPPPDD................." +
        "|" +
        "..........DDDDD................." +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................",
    )

    // economy: green treat/coin circle
    this.pixelDraw(
      TEX.ICON_UPGRADE_ECONOMY,
      32,
      32,
      {
        G: [0x22c55e, 1],
        D: [0x166534, 1],
        L: [0x86efac, 1],
        W: [0xffffff, 1],
      },
      "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "..........DDDDDDD..............." +
        "|" +
        "........DDGGGGGGGDD............." +
        "|" +
        ".......DGGLLLGGGGGD............." +
        "|" +
        "......DGGWLGGGGGGGGD............" +
        "|" +
        "......DGGLLGGGGGGGD............." +
        "|" +
        ".....DGGGGGDDDDGGGD............." +
        "|" +
        ".....DGGGGD....DGGD............." +
        "|" +
        ".....DGGGD......DGD............." +
        "|" +
        ".....DGGGGD....DGGD............." +
        "|" +
        "......DGGGGGDDDDGGGD............" +
        "|" +
        "......DGGGGGGGGGGGGD............" +
        "|" +
        ".......DGGGGGGGGGD.............." +
        "|" +
        "........DDGGGGGDD..............." +
        "|" +
        "..........DDDDDDD..............." +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................",
    )

    // crit: red crosshair star
    this.pixelDraw(
      TEX.ICON_UPGRADE_CRIT,
      32,
      32,
      {
        R: [0xef4444, 1],
        D: [0x991b1b, 1],
        Y: [0xfbbf24, 1],
        W: [0xffffff, 1],
      },
      "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "..............DD................" +
        "|" +
        ".............DRRD..............." +
        "|" +
        "............DRRRD..............." +
        "|" +
        "...........DRRRRRD.............." +
        "|" +
        "..........DRRRRRRD.............." +
        "|" +
        ".........DRRRRRRRD.............." +
        "|" +
        "..DDDDDDDRRRRWRRRDDDDDDD......." +
        "|" +
        "..DRRRRRRRRRRWWRRRRRRRRD........" +
        "|" +
        "..DDDDDDDRRRRWRRRDDDDDDD......." +
        "|" +
        ".........DRRRRRRRD.............." +
        "|" +
        "..........DRRRRRRD.............." +
        "|" +
        "...........DRRRRRD.............." +
        "|" +
        "............DRRRD..............." +
        "|" +
        ".............DRRD..............." +
        "|" +
        "..............DD................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................",
    )

    // range: cyan expanding circle
    this.pixelDraw(
      TEX.ICON_UPGRADE_RANGE,
      32,
      32,
      {
        C: [0x06b6d4, 1],
        D: [0x0e4d5c, 1],
        L: [0x67e8f9, 1],
      },
      "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "..........DDDDDDD..............." +
        "|" +
        "........DD.......DD............." +
        "|" +
        ".......D...........D............" +
        "|" +
        "......D....CCCCC....D..........." +
        "|" +
        ".....D...CC.....CC...D.........." +
        "|" +
        ".....D..C...LLL...C..D.........." +
        "|" +
        "....D..C..LL...LL..C..D........." +
        "|" +
        "....D.C..L...L...L..C.D........." +
        "|" +
        "....D..C..LL...LL..C..D........." +
        "|" +
        ".....D..C...LLL...C..D.........." +
        "|" +
        ".....D...CC.....CC...D.........." +
        "|" +
        "......D....CCCCC....D..........." +
        "|" +
        ".......D...........D............" +
        "|" +
        "........DD.......DD............." +
        "|" +
        "..........DDDDDDD..............." +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................" +
        "|" +
        "................................",
    )
  }

  private createEquipmentTextures(): void {
    // ── Jetpack (16×20) — small SNES-style jetpack strapped to dog's back ──
    this.pixelDraw(
      TEX.JETPACK,
      16,
      20,
      {
        D: [0x334155, 1], // dark steel frame
        S: [0x64748b, 1], // steel mid
        L: [0x94a3b8, 1], // steel highlight
        W: [0xcbd5e1, 1], // white specular
        R: [0xdc2626, 1], // red accent
        r: [0xef4444, 1], // red highlight
        B: [0x1e3a5f, 1], // dark blue tank
        b: [0x2563eb, 1], // blue tank mid
        N: [0x1e293b, 1], // nozzle dark
        n: [0x475569, 1], // nozzle mid
      },
      "....DDDDDD......" +
        "|" +
        "...DSSLLSSD....." +
        "|" +
        "..DSLWWWLSD....." +
        "|" +
        "..DSLWWLSDb....." +
        "|" +
        ".DDSSLLSSDBb...." +
        "|" +
        ".DRrSSSSDBBb...." +
        "|" +
        ".DRrSSSSDBBb...." +
        "|" +
        ".DDSSSSSSBBb...." +
        "|" +
        "..DSSLLSSBb....." +
        "|" +
        "..DSLWWLSBb....." +
        "|" +
        "..DSLWWLSD......" +
        "|" +
        "..DSSLLSSD......" +
        "|" +
        "..DDSSSSDD......" +
        "|" +
        "...DDSSDDD......" +
        "|" +
        "...DNnnNDD......" +
        "|" +
        "...DNnnND......." +
        "|" +
        "....NnnN........" +
        "|" +
        "....NnnN........" +
        "|" +
        ".....Nn........." +
        "|" +
        ".....N..........",
    )
  }

  private createBackgroundNoiseTexture(): void {
    if (this.scene.textures.exists(TEX.BG_NOISE)) return

    const texture = this.scene.textures.createCanvas(TEX.BG_NOISE, 128, 128)
    if (!texture) return

    const context = texture.context

    context.clearRect(0, 0, 128, 128)

    for (let y = 0; y < 128; y += 4) {
      context.fillStyle =
        y % 8 === 0 ? "rgba(255,255,255,0.018)" : "rgba(10,14,24,0.045)"
      context.fillRect(0, y, 128, 1)
    }

    const speckColors = [
      "rgba(255,255,255,0.08)",
      "rgba(186,230,253,0.08)",
      "rgba(196,181,253,0.07)",
      "rgba(244,114,182,0.05)",
    ]

    for (let i = 0; i < 180; i += 1) {
      const x = Math.floor(Math.random() * 128)
      const y = Math.floor(Math.random() * 128)
      const size = Math.random() > 0.88 ? 2 : 1
      context.fillStyle = speckColors[i % speckColors.length]
      context.fillRect(x, y, size, 1)
    }

    for (let i = 0; i < 8; i += 1) {
      const x = Math.floor(Math.random() * 128)
      const y = Math.floor(Math.random() * 128)
      context.fillStyle = "rgba(255,255,255,0.12)"
      context.fillRect(x, y, 1, 1)
    }

    texture.refresh()
  }
}
